import React, { useState } from "react";
import { Shield, AlertTriangle, CheckCircle, Eye, ChevronDown, ChevronUp, FileText } from "lucide-react";

const SOURCE_BADGES = {
  "MHA UAPA": { color: "#dc2626", bg: "rgba(220,38,38,0.12)" },
  "RBI Defaulters": { color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
  "SEBI Debarred": { color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  "ED Press": { color: "#8B0000", bg: "rgba(139,0,0,0.12)" },
  "CBI Wanted": { color: "#dc2626", bg: "rgba(220,38,38,0.1)" },
  "eCourts": { color: "#FF6B35", bg: "rgba(255,107,53,0.1)" },
  "MCA21": { color: "#2563eb", bg: "rgba(37,99,235,0.1)" },
  "NCLT": { color: "#f59e0b", bg: "rgba(245,158,11,0.08)" },
  "GSTN": { color: "#10b981", bg: "rgba(16,185,129,0.1)" },
  "Sanctions.io": { color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
  "PEP": { color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  "Adverse Media": { color: "#FF6B35", bg: "rgba(255,107,53,0.1)" },
};

function generateMockSourceMatches(result) {
  const sources = [];
  if (result?.sanctions_result?.status === "match") {
    sources.push({ source: "Sanctions.io", confidence: 87, detail: "Name match on global sanctions list" });
    sources.push({ source: "MHA UAPA", confidence: 72, detail: "Partial name match on UAPA designated list" });
  }
  if (result?.pep_result?.status === "match") {
    sources.push({ source: "MCA21", confidence: 65, detail: "Director of company with political connections" });
  }
  if (result?.adverse_media_result?.status === "hits_found") {
    sources.push({ source: "ED Press", confidence: 78, detail: "Named in enforcement directorate press release" });
    sources.push({ source: "eCourts", confidence: 55, detail: "Criminal case registered under IPC sections" });
  }
  if (sources.length === 0) {
    sources.push({ source: "GSTN", confidence: 92, detail: "Business verification — active registration" });
  }
  return sources;
}

function generateAISummary(result) {
  const name = result?.full_name || "the subject";
  const score = result?.risk_score || 0;
  if (score > 70) {
    return `${name} presents HIGH risk based on multiple source matches. Sanctions screening returned positive hits, and adverse media mentions were found in enforcement directorate filings. Immediate enhanced due diligence and senior management review is recommended before proceeding.`;
  } else if (score > 40) {
    return `${name} shows MEDIUM risk indicators. Partial matches found in screening databases require manual review to confirm identity. Recommend standard due diligence completion with periodic monitoring schedule of 90 days.`;
  }
  return `${name} shows LOW risk with no significant adverse findings across screened databases. Standard CDD procedures apply. Next periodic review recommended in 12 months per RBI guidelines.`;
}

export function EnhancedResultCard({ result, onClear, onEscalate, onWatchlist }) {
  const [expandedSource, setExpandedSource] = useState(null);

  if (!result) return null;

  const score = result.risk_score || 0;
  const sourceMatches = generateMockSourceMatches(result);
  const aiSummary = generateAISummary(result);
  const scoreColor = score > 70 ? "#dc2626" : score > 50 ? "#ef4444" : score > 25 ? "#f59e0b" : "#10b981";

  return (
    <div data-testid="enhanced-result-card" className="card-aml" style={{ marginBottom: "16px" }}>
      {/* Match Score + Name */}
      <div className="flex items-center gap-4 mb-4">
        <div data-testid="match-score-badge" style={{
          width: "64px", height: "64px", borderRadius: "50%",
          background: `${scoreColor}15`, border: `3px solid ${scoreColor}`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: "20px", fontWeight: 700, color: scoreColor, lineHeight: 1 }}>{score}</span>
          <span style={{ fontSize: "8px", color: scoreColor, fontWeight: 600 }}>/ 100</span>
        </div>
        <div>
          <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#f1f5f9" }}>{result.full_name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span style={{
              fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px",
              background: score > 50 ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)",
              color: score > 50 ? "#ef4444" : "#10b981", textTransform: "uppercase",
            }}>
              {result.risk_level || (score > 70 ? "HIGH" : score > 40 ? "MEDIUM" : "LOW")}
            </span>
            <span style={{ fontSize: "11px", color: "#475569" }}>{result.nationality || "IN"}</span>
            {result.status === "flagged" && (
              <span style={{ fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "3px", background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
                FLAGGED
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Source Badges */}
      <div style={{ marginBottom: "12px" }}>
        <label style={{ fontSize: "10px", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: "6px" }}>
          Sources Matched
        </label>
        <div className="flex flex-wrap gap-2">
          {sourceMatches.map((sm) => {
            const badge = SOURCE_BADGES[sm.source] || { color: "#94a3b8", bg: "rgba(148,163,184,0.1)" };
            const isExpanded = expandedSource === sm.source;
            return (
              <div key={sm.source}>
                <button
                  onClick={() => setExpandedSource(isExpanded ? null : sm.source)}
                  data-testid={`source-badge-${sm.source.replace(/\s/g, "-").toLowerCase()}`}
                  style={{
                    display: "flex", alignItems: "center", gap: "5px",
                    padding: "4px 10px", borderRadius: "6px",
                    background: badge.bg, border: `1px solid ${badge.color}25`,
                    cursor: "pointer", transition: "all 0.15s",
                  }}>
                  <span style={{ fontSize: "10px", fontWeight: 700, color: badge.color }}>{sm.source}</span>
                  <span style={{ fontSize: "9px", color: badge.color, opacity: 0.7 }}>{sm.confidence}%</span>
                  {isExpanded ? <ChevronUp style={{ width: 10, height: 10, color: badge.color }} /> : <ChevronDown style={{ width: 10, height: 10, color: badge.color }} />}
                </button>
                {isExpanded && (
                  <div style={{ marginTop: "4px", padding: "8px 10px", borderRadius: "6px", background: "#080c12", border: "1px solid #1e2530", fontSize: "11px", color: "#94a3b8" }}>
                    {sm.detail}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Risk Summary */}
      <div data-testid="ai-risk-summary" style={{
        padding: "12px 14px", borderRadius: "8px", marginBottom: "12px",
        background: "rgba(37,99,235,0.05)", border: "1px solid rgba(37,99,235,0.15)",
      }}>
        <div className="flex items-center gap-2 mb-2">
          <Shield style={{ width: "12px", height: "12px", color: "#60a5fa" }} />
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            AI Risk Summary
          </span>
        </div>
        <p style={{ fontSize: "12px", color: "#94a3b8", lineHeight: 1.6, margin: 0 }}>{aiSummary}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2" data-testid="result-actions">
        <button onClick={onClear} data-testid="action-clear" style={actionBtnStyle("#10b981", "rgba(16,185,129,0.08)")}>
          <CheckCircle style={{ width: "13px", height: "13px" }} /> Clear
        </button>
        <button onClick={onEscalate} data-testid="action-escalate" style={actionBtnStyle("#f59e0b", "rgba(245,158,11,0.08)")}>
          <AlertTriangle style={{ width: "13px", height: "13px" }} /> Escalate
        </button>
        <button onClick={onWatchlist} data-testid="action-watchlist" style={actionBtnStyle("#2563eb", "rgba(37,99,235,0.08)")}>
          <Eye style={{ width: "13px", height: "13px" }} /> Add to Watchlist
        </button>
      </div>
    </div>
  );
}

function actionBtnStyle(color, bg) {
  return {
    display: "flex", alignItems: "center", gap: "5px",
    padding: "7px 14px", borderRadius: "8px", cursor: "pointer",
    background: bg, border: `1px solid ${color}30`,
    color, fontSize: "12px", fontWeight: 600, transition: "all 0.15s",
  };
}
